import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { emit } from "process";
import { CartActions } from "./cart.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, EMPTY, filter, map, merge, of, switchMap, withLatestFrom } from "rxjs";
import { CartService } from "projects/shared/services/cart.service";
import { SelectRegionalCurrency } from "../../nav-regional/store/regional-setting.selectors";
import { SelectCart, SelectCartItems } from "./cart.selectors";
import { RegionalActions } from "../../nav-regional/store/regional-setting.actions";

@Injectable()
export class CartEffect {
  store = inject(Store);
  actions$ = inject(Actions);
  cartService = inject(CartService);
  currency$ = this.store.selectSignal(SelectRegionalCurrency);
  cartItems$ = this.store.selectSignal(SelectCartItems);
  cart$ = this.store.selectSignal(SelectCart);

  cartEffect$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(CartActions.initializeCart)),
      this.actions$.pipe(ofType(RegionalActions.loadData)),
      this.actions$.pipe(ofType(RegionalActions.updateData)),
    ).pipe(
      switchMap(() => {
        const currency = this.currency$();
        const cart = this.cart$();

        if (!currency) {
          return of(CartActions.getCartFailure({ getCartFailure: 'Currency not set' }));
        }

        const cartNotExists = !cart;
        const currencyMismatch = cart && cart.currency !== currency;

        if (!cartNotExists && !currencyMismatch) {
          return EMPTY;
        }

        return this.cartService.getCart(currency).pipe(
          switchMap(res => [
            CartActions.getCartSuccess({ getCartSuccess: "Get Cart Successfully" }),
            CartActions.getCartData({ cart: res }),
            CartActions.getCartProducts({ items: res.products })
          ]),
          catchError((error) => of(CartActions.getCartFailure({ getCartFailure: error.message })))
        );
      })
    )
  )

  itemEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.addItem, CartActions.updateCartItem, CartActions.mergeCartItems),
      switchMap((action) => {
        if (action.type === CartActions.addItem.type) {
          this.store.dispatch(CartActions.updateCartSuccess({ updateCartSuccess: 'Product Added Successfully' }));
        } else if (action.type === CartActions.updateCartItem.type) {
          this.store.dispatch(CartActions.updateCartSuccess({ updateCartSuccess: 'Product Updated Successfully' }))
        } else if (action.type === CartActions.mergeCartItems.type) {
          this.store.dispatch(CartActions.updateCartSuccess({ updateCartSuccess: 'Products Updated Successfully' }))
        }

        return this.cartService.addToCart(this.cartItems$()).pipe(
          switchMap(() => this.cartService.getCart(this.currency$())),
          switchMap(res => [
            CartActions.getCartData({ cart: res }),
            CartActions.getCartProducts({ items: res.products })
          ]),
          catchError((error) => of(CartActions.updateCartFailure({ updateCartFailure: error.message })))
        );
      })
    )
  );

  removeItemEffect$ = createEffect(() => this.actions$.pipe(
    ofType(CartActions.removeItem),
    switchMap(() => this.cartService.addToCart(this.cartItems$()).pipe(
      switchMap(() => this.cartService.getCart(this.currency$())),
      switchMap(res => [
        CartActions.updateCartSuccess({ updateCartSuccess: 'Product Removed Successfully' }),
        CartActions.updateCart({ cart: res }),
        CartActions.getCartProducts({ items: res.products })
      ]),
      catchError((error) => [CartActions.updateCartFailure({ updateCartFailure: error.message })])
    ))
  ))


}